/**
 * 패널 계층 — API DTO ↔ Domain 변환 (경계 전용)
 *
 * === 레이어 규칙 ===
 * - API: Java DTO 그대로 (`id`, `stringId`, `arrayId`, `moduleId`)
 * - Domain: 프론트 의미 명확 (`seq`, `displayNo`)
 * - Map/Tree: panelMapFeatureProps · PanelEntityId 가 Domain 소비
 *
 * Domain 필드는 API 레이어 밖에서만 사용한다.
 */

/** @typedef {{ seq: number, displayNo: string|null, name: string|null, shapeGeog?: *, rowNum?: number|null, colNum?: number|null, description?: string|null }} PanelStringDomain */
/** @typedef {{ seq: number, displayNo: string|null, name: string|null, shapeGeog?: * }} PanelArrayDomain */
/** @typedef {{ seq: number, displayNo: string|null, shapeGeog?: *, centerCoord?: * }} PanelModuleDomain */

function toDisplayNo(value) {
  if (value == null || value === '') {
    return null;
  }
  return String(value);
}

function toSeq(value) {
  if (value == null || value === '') {
    return null;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** SolarStringDto → Domain */
export function stringFromApi(dto = {}) {
  return {
    seq: toSeq(dto.id ?? dto.seq),
    displayNo: toDisplayNo(dto.stringId ?? dto.displayNo),
    name: dto.name ?? null,
    rowNum: dto.rowNum ?? null,
    colNum: dto.colNum ?? null,
    description: dto.description ?? null,
    shapeGeog: dto.shapeGeog ?? null
  };
}

/** Domain → SolarStringDto (POST/PATCH body) */
export function stringToApi(domain = {}) {
  const payload = {};
  if (domain.seq != null) payload.id = domain.seq;
  if (domain.displayNo != null) payload.stringId = domain.displayNo;
  if (domain.name != null) payload.name = domain.name;
  if (domain.rowNum != null) payload.rowNum = domain.rowNum;
  if (domain.colNum != null) payload.colNum = domain.colNum;
  if (domain.description != null) payload.description = domain.description;
  if (domain.shapeGeog != null) payload.shapeGeog = domain.shapeGeog;
  return payload;
}

/** SolarArrayDto → Domain */
export function arrayFromApi(dto = {}) {
  return {
    seq: toSeq(dto.id ?? dto.seq),
    displayNo: toDisplayNo(dto.arrayId ?? dto.displayNo),
    name: dto.name ?? null,
    shapeGeog: dto.shapeGeog ?? null
  };
}

/** Domain → SolarArrayDto */
export function arrayToApi(domain = {}) {
  const payload = {};
  if (domain.seq != null) payload.id = domain.seq;
  if (domain.displayNo != null) payload.arrayId = domain.displayNo;
  if (domain.name != null) payload.name = domain.name;
  if (domain.shapeGeog != null) payload.shapeGeog = domain.shapeGeog;
  return payload;
}

/** SolarModuleDto → Domain */
export function moduleFromApi(dto = {}) {
  return {
    seq: toSeq(dto.id ?? dto.seq),
    displayNo: toDisplayNo(dto.moduleId ?? dto.displayNo),
    shapeGeog: dto.shapeGeog ?? null,
    centerCoord: dto.centerCoord ?? null
  };
}

/** Domain → SolarModuleDto */
export function moduleToApi(domain = {}) {
  const payload = {};
  if (domain.seq != null) payload.id = domain.seq;
  if (domain.displayNo != null) payload.moduleId = domain.displayNo;
  if (domain.shapeGeog != null) payload.shapeGeog = domain.shapeGeog;
  if (domain.centerCoord != null) payload.centerCoord = domain.centerCoord;
  return payload;
}

/** 평면화/트리 레코드 → Domain (id/stringId 별칭 지원) */
export function stringFromFlatRecord(record = {}) {
  if (record.seq != null && record.displayNo != null) {
    return {
      seq: toSeq(record.seq),
      displayNo: toDisplayNo(record.displayNo),
      name: record.name ?? null,
      rowNum: record.rowNum ?? null,
      colNum: record.colNum ?? null,
      description: record.description ?? null,
      shapeGeog: record.shapeGeog ?? null
    };
  }
  return stringFromApi(record);
}

/** Domain → 트리 String 노드 공통 필드 */
export function stringDomainToTreeFields(domain) {
  const seq = domain?.seq;
  const displayNo = domain?.displayNo;
  return {
    id: seq != null ? `string_${seq}` : undefined,
    type: 'string',
    seq,
    displayNo,
    name: domain?.name ?? (displayNo != null ? `String ${displayNo}` : 'String'),
    solarStringSeq: seq,
    solarStringId: displayNo,
    stringId: displayNo
  };
}

/** Domain → 트리 Array 노드 공통 필드 */
export function arrayDomainToTreeFields(domain) {
  const seq = domain?.seq;
  const displayNo = domain?.displayNo;
  return {
    seq,
    displayNo,
    solarArraySeq: seq,
    arrayId: displayNo,
    solarArrayId: displayNo
  };
}

/** Domain → 트리 Module 노드 공통 필드 */
export function moduleDomainToTreeFields(domain) {
  const seq = domain?.seq;
  const displayNo = domain?.displayNo;
  return {
    id: seq != null ? `module_${seq}` : undefined,
    type: 'module',
    seq,
    displayNo,
    name: displayNo != null ? `Module ${displayNo}` : 'Module',
    solarModuleSeq: seq,
    solarModuleId: displayNo,
    moduleId: displayNo
  };
}

/** 트리 String 노드 → DB seq (전역 고유) */
export function readStringSeqFromTreeItem(item) {
  if (!item) {
    return null;
  }
  if (item.seq != null && item.seq !== '') {
    return String(item.seq);
  }
  if (item.solarStringSeq != null && item.solarStringSeq !== '') {
    return String(item.solarStringSeq);
  }
  if (typeof item.id === 'string' && item.id.startsWith('string_')) {
    return item.id.slice('string_'.length);
  }
  return null;
}

/** 트리 String 노드 → Array 내 표시 번호 */
export function readStringDisplayNoFromTreeItem(item) {
  if (!item) {
    return null;
  }
  const raw = item.displayNo ?? item.stringId ?? item.solarStringId;
  return raw != null && raw !== '' ? String(raw) : null;
}
